import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import API from "@/lib/api";
import { categoryLabel, formatPrice } from "@/lib/data";
import { FadeUp } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState("loading");
  const [related, setRelated] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImg, setActiveImg] = useState(null);

  useEffect(() => {
    setState("loading");
    setSelectedSize(null);
    setActiveImg(null);
    window.scrollTo(0, 0);
    API.get(`/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setSelectedSize(res.data.variants?.length ? res.data.variants[0] : null);
        setState("ready");
        API.get("/products").then((r) => {
          setRelated(
            r.data.filter((p) => p.category === res.data.category && p.id !== res.data.id).slice(0, 3)
          );
        });
      })
      .catch(() => setState("missing"));
  }, [id]);

  if (state === "loading") {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center" data-testid="product-loading">
        <p className="font-display italic text-2xl text-mist">Fetching from the cabinet&hellip;</p>
      </main>
    );
  }

  if (state === "missing" || !product) {
    return (
      <main className="pt-16 min-h-screen flex flex-col items-center justify-center gap-6" data-testid="product-not-found">
        <p className="font-display italic text-3xl text-mist">That piece has wandered off.</p>
        <Link to="/shop" data-testid="product-back-to-shop" className="text-[13px] uppercase tracking-widest2 text-gold border-b border-gold/40 pb-1">
          Back to the shop
        </Link>
      </main>
    );
  }

  const stockLine =
    product.stock === 0
      ? "Out of the cabinet — enquire for the next batch"
      : product.stock === 1
      ? "One of one — when it's gone, it's gone"
      : `${product.stock} in the cabinet`;

  const price = selectedSize ? selectedSize.price : product.price;
  const displayImage = (selectedSize && selectedSize.image_url) || activeImg || product.image_url;
  const gallery = [product.image_url, ...(product.images || [])];
  const enquirySubject = selectedSize
    ? `Enquiry — ${product.name} (${selectedSize.size})`
    : `Enquiry — ${product.name}`;

  return (
    <main className="pt-16" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 md:py-20">
        <Link
          to="/shop"
          data-testid="product-detail-back-link"
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 text-mist transition-colors duration-300 hover:text-gold"
        >
          <ArrowLeft size={14} /> The Cabinet
        </Link>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-start">
          <FadeUp className="relative spotlight">
            <div className="overflow-hidden rounded-xl border border-line">
              <img
                src={displayImage}
                alt={product.name}
                data-testid="product-detail-image"
                className="w-full h-[420px] md:h-[640px] object-cover"
              />
            </div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="product-gallery">
                {gallery.map((g, i) => (
                  <button
                    key={g}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => { setActiveImg(g); setSelectedSize(null); }}
                    aria-label={`View photo ${i + 1}`}
                    className={`shrink-0 h-20 w-20 rounded-lg overflow-hidden border transition-all duration-300 ${
                      displayImage === g ? "border-gold opacity-100" : "border-line opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={g} alt={`${product.name} — view ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </FadeUp>

          <div>
            <FadeUp>
              <p className="text-[12px] uppercase tracking-widest2 text-gold">{categoryLabel(product.category)}</p>
              <h1
                data-testid="product-detail-name"
                className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.2rem)] leading-[1.02]"
              >
                {product.name}
              </h1>
              <p data-testid="product-detail-price" className="mt-5 font-display text-3xl text-gold">
                {formatPrice(price)}
              </p>
            </FadeUp>

            {product.variants?.length > 0 && (
              <FadeUp delay={0.05}>
                <p className="mt-6 text-[11px] uppercase tracking-widest2 text-mist">{product.variant_label || "Size"}</p>
                <div className="mt-3 flex flex-wrap gap-3" data-testid="product-size-selector">
                  {product.variants.map((v) => (
                    <button
                      key={v.size}
                      data-testid={`size-pill-${v.size.toLowerCase()}`}
                      onClick={() => setSelectedSize(v)}
                      className={`rounded-full px-6 py-2.5 text-[12px] uppercase tracking-widest2 border transition-colors duration-300 ${
                        selectedSize?.size === v.size
                          ? "bg-gold text-ink border-gold font-semibold"
                          : "border-line text-mist hover:border-gold/50 hover:text-gold"
                      }`}
                    >
                      {v.size} &middot; {formatPrice(v.price)}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-mist/60" data-testid="product-size-note">
                  Sizes depend on the stock we have — feel free to reach out in the{" "}
                  <Link to="/#queries" className="text-sage border-b border-sage/40 pb-0.5 transition-colors duration-300 hover:text-gold">
                    query section
                  </Link>{" "}
                  for a size description.
                </p>
              </FadeUp>
            )}

            <FadeUp delay={0.1}>
              <p data-testid="product-detail-description" className="mt-8 text-mist leading-relaxed text-base max-w-md">
                {product.description || "A piece from the moonspiritvida cabinet."}
              </p>
              <p data-testid="product-detail-stock" className="mt-6 text-[12px] uppercase tracking-widest2 text-sage">
                {stockLine}
              </p>
              <p data-testid="product-price-note" className="mt-3 text-xs text-mist/60 italic font-display text-sm">
                *Prices may vary according to the stone.
              </p>
            </FadeUp>

            <FadeUp delay={0.2} className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href={`mailto:moonspiritvida@gmail.com?subject=${encodeURIComponent(enquirySubject)}`}
                data-testid="product-enquire-button"
                className="inline-flex items-center rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper"
              >
                Enquire about this piece
              </a>
              <Link
                to="/tarot"
                data-testid="product-tarot-link"
                className="text-[13px] uppercase tracking-widest2 text-paper border-b border-gold/40 pb-1.5 transition-colors duration-300 hover:text-gold"
              >
                Or book a reading
              </Link>
            </FadeUp>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-28" data-testid="product-related-section">
            <FadeUp>
              <h2 className="font-display font-light tracking-tighter text-3xl md:text-4xl">
                More from <span className="italic text-gold">{categoryLabel(product.category)}</span>
              </h2>
            </FadeUp>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {related.map((p, i) => (
                <FadeUp key={p.id} delay={i * 0.08}>
                  <ProductCard product={p} index={i} />
                </FadeUp>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
